import { useMemo } from "react";
import { useGetConnectionStatusQuery } from "services/quickbooks-api";

/**
 * hook for managing QuickBooks connection status
 * provides connection status information and helper functions
 */
export const useQuickBooksConnectionStatus = () => {
  const {
    data: connectionStatus,
    isLoading,
    refetch,
    ...restQuery
  } = useGetConnectionStatusQuery(undefined, { skip: false });

  /**
   * check if QuickBooks connection is valid for operations
   * returns null when loading to differentiate from false (invalid)
   */
  const isConnectionValid = useMemo(() => {
    if (isLoading) return null;
    if (!connectionStatus?.is_connected) return false;
    // if there's an error message, the connection needs renewal
    if (connectionStatus.error_message) return false;
    return true;
  }, [connectionStatus, isLoading]);

  /**
   * generate appropriate message for QuickBooks connection issues
   */
  const connectionMessage = useMemo(() => {
    if (isLoading) {
      return "Loading QuickBooks connection status...";
    }

    if (!connectionStatus) {
      return "Checking QuickBooks connection status...";
    }

    if (!connectionStatus.is_connected) {
      return "Connect to QuickBooks in Settings to use this feature";
    }

    if (connectionStatus.error_message) {
      return connectionStatus.error_message;
    }

    return "Connected to QuickBooks";
  }, [connectionStatus, isLoading]);

  /**
   * get detailed connection status information for debugging
   */
  const getDetailedStatus = useMemo(() => {
    if (isLoading || !connectionStatus) return null;

    return {
      isConnected: connectionStatus.is_connected,
      isValid: isConnectionValid,
      statusInfo: connectionStatus.token_status
        ? `Status: ${connectionStatus.token_status}`
        : "",
      errorInfo: connectionStatus.error_message
        ? `Error: ${connectionStatus.error_message}`
        : "",
      needsRenewal: !!connectionStatus.error_message,
      tokenStatus: connectionStatus.token_status,
    };
  }, [connectionStatus, isConnectionValid, isLoading]);

  /**
   * get a simple status text for displaying connection status
   */
  const getStatusText = useMemo(() => {
    if (isLoading) return "Loading...";
    if (!connectionStatus) return "";

    if (connectionStatus.error_message) {
      return "⚠️ Needs Renewal";
    }

    if (connectionStatus.is_connected) {
      return "✓ Connected";
    }

    return "";
  }, [connectionStatus, isLoading]);

  return {
    connectionStatus,
    isLoading,
    isConnectionValid,
    connectionMessage,
    getDetailedStatus,
    getStatusText,
    refetch,
    ...restQuery,
  };
};

export default useQuickBooksConnectionStatus;
